<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="page-titlerow">
          <h2 class="d-flex align-items-center" style="gap: 0.6rem;">
            <span>{{ headerTitle }}</span>
            <span v-if="!leagueConfig.active" class="badge bg-secondary" style="font-size: 0.7rem;">Inactive</span>
          </h2>
          <button type="button" class="page-helpbtn" :class="{ 'is-open': helpOpen }" :aria-expanded="helpOpen" @click="helpOpen = !helpOpen">
            <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
          </button>
        </div>

        <div v-show="helpOpen" class="page-help mb-3">
          <ul>
            <li>Confirmed matches for this calendar. Use <strong>Edit</strong> on a match to correct its teams, venue,
              timing, format or Cal Status (e.g. mark it cancelled), re-upload the calendar to update the whole
              schedule, or use <strong>Add match</strong> for a fixture that isn't in its
              .ics file. Expand a match below to set up its sources.</li>
            <li>Click a match's <strong>Sources</strong> count to expand it and configure the channels/encoder
              streams that carry this match. Timing is set as <strong>Lead</strong> (minutes before the match
              starts) and <strong>Trail</strong> (minutes after it ends).</li>
            <li><strong>Channel:</strong> a new source starts on <strong>Automatic</strong> and the system gives it
              a free channel. To choose one yourself, pick it from the Channel list, and you can change it later
              the same way. The list marks channels that are already taken in that time window.</li>
            <li><strong>Order</strong> is a source's position within its match. It is set and edited on the
              <strong>Daily</strong> page. Two sources of one match can share an order; you only get a
              yellow "same order" warning, nothing is blocked.</li>
            <li><strong>Upcoming</strong> shows active matches that have not ended yet, <strong>All</strong> shows the
              whole calendar. A finished match is read-only. <strong>Show conflicts</strong> lists only matches
              with a possible conflict.</li>
            <li><span class="legend-swatch legend-swatch--conflict"></span> Red on a Sources count or a source
              itself means a possible conflict: that channel or encoder stream overlaps another match's
              assignment elsewhere — click the "⚠ conflict" badge to jump straight to it.</li>
          </ul>
        </div>

        <div class="box-outline p-3 mb-3">
          <h5 role="button" class="d-flex align-items-center justify-content-between mb-0" @click="defaultSourcesOpen = !defaultSourcesOpen">
            <span>Default sources</span>
            <span class="chevron">{{ defaultSourcesOpen ? '▾' : '▸' }}</span>
          </h5>
          <div v-show="defaultSourcesOpen" class="mt-2">
            <p class="small text-muted mb-2">
              Set up to 4 default sources here: for each one the origin, language, encoder stream, and lead
              (minutes before match start) and trail (minutes after match end). Leave the channel on
              <strong>Automatic</strong> and every match gets its own free channel, so matches in the same time
              window never share one. <strong>Apply to upcoming matches</strong> sets the sources on every active
              match of this calendar that has not started yet, as real stream start/end times. A match is skipped, and listed under the
              button, if it overlaps another match of this calendar on the same encoder stream, if no channel is
              free, or if it clashes with a source that already exists. Each match can still be overridden,
              reset back to this default, or switched off individually.
            </p>
            <CalChannelSlotEditor
                v-for="n in visibleTemplateSlotCount" :key="'template-' + n"
                :source-index="n"
                :channel="templateSlots[n]"
                :channel-options="templateChannelOptionsForRow(n)"
                :hardcoded-data="hardcodedData"
                :is-template="true"
                :saving="!!templateSaving[n]"
                :error-message="templateErrors[n] || ''"
                :default-lead-minutes="calendarStatic.defaultLeadMinutes"
                :default-trail-minutes="calendarStatic.defaultTrailMinutes"
                :language-options="calendarStatic.languages"
                :locked="isTemplateSourceLocked(n)"
                @save="onTemplateSlotSave">
            </CalChannelSlotEditor>
            <div v-if="applyToAllError !== ''" class="error-message mt-2 text-end apply-to-all-error">{{ applyToAllError }}</div>
            <div v-if="applyResult" class="apply-result mt-2">
              <div class="apply-result-ok">
                Default sources set on {{ applyResult.matchesApplied }} match{{ applyResult.matchesApplied === 1 ? '' : 'es' }}
                ({{ applyResult.sourcesWritten }} source{{ applyResult.sourcesWritten === 1 ? '' : 's' }}).
              </div>
              <div v-if="applyResult.notSet.length" class="apply-result-warn">
                <div class="fw-semibold">&#9888; {{ applyResult.notSet.length }} match{{ applyResult.notSet.length === 1 ? ' was' : 'es were' }} not set, conflicts:</div>
                <ul class="mb-0">
                  <li v-for="m in applyResult.notSet" :key="m.calMatchId">
                    #{{ m.calMatchId }} &middot; {{ m.team1 }} vs {{ m.team2 }} ({{ formatDateTime(m.startTime) }}) &mdash;
                    {{ notSetReasonLabel(m.reason) }}: {{ m.details }}
                  </li>
                </ul>
              </div>
            </div>
            <!-- Add source on the left, Apply to all on the right. -->
            <div class="d-flex align-items-center mt-2" style="gap: 0.75rem;">
              <a v-if="visibleTemplateSlotCount < 4" role="button" class="text-link-sm" @click="addTemplateSlot">
                + Add source
              </a>
              <a role="button" class="procricket-btn ms-auto" :class="{ disabled: applyingToAll || !hasAnyTemplateSlot || upcomingActiveMatchCount === 0 }"
                 title="Only matches that have not started yet are changed" @click="applyTemplateToAll">
                {{ applyingToAll ? 'Applying...' : 'Apply to upcoming matches (' + upcomingActiveMatchCount + ')' }}
              </a>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center matches-toolbar">
          <div class="d-flex align-items-center toolbar-btns" style="gap: 0.75rem;">
            <a role="button" class="procricket-btn--reverse mb-3" @click="backToCalendars">Back to Calendars</a>
            <a role="button" class="procricket-btn mb-3" @click="toggleAddMatch">
              {{ addMatchOpen ? 'Close' : '+ Add match' }}
            </a>
            <a role="button" class="procricket-btn--reverse mb-3" @click="toggleAllSourcesExpand">
              {{ allSourcesExpanded ? 'Collapse' : 'Expand' }} All Sources
            </a>
            <a role="button" class="procricket-btn--reverse mb-3" :class="{ disabled: !showConflictsOnly && conflictMatchCount === 0 }" @click="toggleShowConflicts">
              {{ showConflictsOnly ? 'Show all matches' : 'Show conflicts (' + conflictMatchCount + ')' }}
            </a>
            <a role="button" class="mb-3" :class="[leagueConfig.active ? 'procricket-btn--danger' : 'procricket-btn--reverse', { disabled: togglingLeagueActive }]" @click="toggleLeagueActive">
              {{ leagueConfig.active ? 'Deactivate calendar' : 'Reactivate calendar' }}
            </a>
            <div class="view-switch mb-3" role="group" aria-label="Matches to show" title="Upcoming = active matches that have not ended yet">
              <a role="button" :class="{ active: matchFilter === 'upcoming' }" @click="matchFilter = 'upcoming'">Upcoming</a>
              <a role="button" :class="{ active: matchFilter === 'all' }" @click="matchFilter = 'all'">All</a>
            </div>
          </div>
          <div class="d-flex align-items-center matches-toolbar-right" :style="{ gap: '1rem', width: '20%' }">
            <input type="text" class="form-control mb-3 match-search-input" placeholder="Search matches..." v-model="searchText" style="flex: 1 1 auto; min-width: 0; position: relative;">
          </div>
        </div>
        <div v-if="addMatchOpen" class="box-outline p-3 mb-3">
          <h5 class="mb-2">Add match</h5>
          <div class="d-flex flex-wrap align-items-end" style="gap: 0.75rem;">
            <div class="form-group mb-0">
              <label class="small mb-0" for="newTeam1">Team 1</label>
              <input id="newTeam1" type="text" class="form-control form-control-sm" style="width: 170px;" v-model="newMatch.team1">
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newTeam2">Team 2</label>
              <input id="newTeam2" type="text" class="form-control form-control-sm" style="width: 170px;" v-model="newMatch.team2">
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newVenue">Venue</label>
              <input id="newVenue" type="text" class="form-control form-control-sm" style="width: 190px;" v-model="newMatch.venue">
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newStart">Start ({{ tz }})</label>
              <input id="newStart" type="datetime-local" class="form-control form-control-sm" style="width: 180px;" v-model="newMatch.startLocal">
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newEnd">End ({{ tz }})</label>
              <input id="newEnd" type="datetime-local" class="form-control form-control-sm" style="width: 180px;" v-model="newMatch.endLocal">
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newFormat">Format</label>
              <select id="newFormat" class="form-select form-select-sm" style="width: 100px;" v-model="newMatch.format">
                <option :value="undefined">—</option>
                <option v-for="f in (calendarStatic.matchFormats || [])" :key="f" :value="f">{{ f }}</option>
              </select>
            </div>
            <div class="form-group mb-0">
              <label class="small mb-0" for="newCalStatus">Cal status</label>
              <select id="newCalStatus" class="form-select form-select-sm" style="width: 130px;" v-model="newMatch.calStatus">
                <option v-for="s in (calendarStatic.calStatuses || ['CONFIRMED'])" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <a role="button" class="procricket-btn btn-xs" :class="{ disabled: addingMatch || !canAddMatch }" @click="addMatch">
              {{ addingMatch ? 'Saving...' : 'Save match' }}
            </a>
            <a role="button" class="procricket-btn--reverse btn-xs" @click="toggleAddMatch">Cancel</a>
          </div>
          <div v-if="addMatchError !== ''" class="error-message mt-2">{{ addMatchError }}</div>
        </div>

        <div v-if="loadError !== ''" class="error-message mb-3">{{ loadError }}</div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered cal-matches-table">
            <thead>
            <tr>
              <th class="col-1" @click="toggleSort('calMatchId')">Id</th>
              <th class="col-1">Day</th>
              <th class="col-1 team-cell" @click="toggleSort('team1')">Team 1</th>
              <th class="col-1 team-cell" @click="toggleSort('team2')">Team 2</th>
              <th class="col-2 team-cell" @click="toggleSort('venue')">Venue</th>
              <th class="col-2" @click="toggleSort('startTime')">Start ({{ tz }})</th>
              <th class="col-2" @click="toggleSort('endTime')">End ({{ tz }})</th>
              <th class="col-1" @click="toggleSort('format')">Format</th>
              <th class="col-1" @click="toggleSort('calStatus')">Cal Status</th>
              <th class="col-1" @click="toggleSort('status')">Status</th>
              <th class="col-1" @click="toggleSort('active')">Active</th>
              <th class="col-1">Sources</th>
              <th class="col-1">Edit</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="match in sortData" :key="match.calMatchId">
              <tr :id="'match-row-' + match.calMatchId" class="match-summary-row" :class="{ 'row-excluded': !match.active, 'row-finished': matchFinished(match), 'row-highlighted': match.calMatchId === highlightedMatchId }">
                <td class="mobile-match-card" :colspan="13" @click="toggleSourcesExpand(match.calMatchId)">
                  <div class="mobile-card-top">
                    <span class="mobile-card-teams text-truncate">{{ truncateText(match.team1, 40) }} <span class="mobile-vs">vs</span> {{ truncateText(match.team2, 40) }}</span>
                    <span class="chevron">{{ isSourcesExpanded(match.calMatchId) ? '▾' : '▸' }}</span>
                  </div>
                  <div class="mobile-card-meta text-muted small">{{ formatShortRange(match) }}</div>
                  <div class="mobile-card-bottom">
                    <span v-if="!match.active" class="badge bg-secondary">Inactive</span>
                    <span v-else></span>
                    <router-link :to="editLink(match)" class="procricket-btn--reverse btn-xs ms-auto me-2" @click.stop>Edit</router-link>
                    <span class="mobile-card-sources" :class="{ 'sources-conflict': hasSourceConflict(match.calMatchId) }">
                      {{ configuredCount(match.calMatchId) }}/4 sources<template v-if="hasSourceConflict(match.calMatchId)"> &#9888;</template>
                    </span>
                  </div>
                </td>
                <td class="col-1 text-truncate">{{ truncateText(match.calMatchId, 30) }}</td>
                <td class="col-1 text-truncate">{{ dayLabel(match) }}</td>
                <td class="col-1 team-cell text-truncate">{{ truncateText(match.team1, 40) }}</td>
                <td class="col-1 team-cell text-truncate">{{ truncateText(match.team2, 40) }}</td>
                <td class="col-2 team-cell text-truncate">{{ truncateText(match.venue, 40) }}</td>
                <td class="col-2 text-nowrap">{{ formatDateTime(match.startTime) }}</td>
                <td class="col-2 text-nowrap">{{ formatDateTime(match.endTime) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.format, 20) }}</td>
                <td class="col-1 text-truncate" @click.stop>
                  <select v-if="editingCalStatusId === match.calMatchId" :ref="'calStatusSelect' + match.calMatchId"
                          class="form-select form-select-sm cal-status-select" :disabled="calStatusSavingId === match.calMatchId"
                          :value="match.calStatus" @change="saveCalStatus(match, $event.target.value)" @blur="editingCalStatusId = null">
                    <option v-for="st in calStatusOptionsFor(match)" :key="st" :value="st">{{ st }}</option>
                  </select>
                  <a v-else role="button" class="cal-status-cell" title="Click to change the cal status" @click="startCalStatusEdit(match)">
                    {{ truncateText(match.calStatus, 20) }} <span class="cal-status-caret">&#9662;</span>
                  </a>
                </td>
                <td class="col-1 text-truncate">{{ truncateText(match.status, 10) }}</td>
                <td class="col-1 text-truncate">{{ truncateText(match.active, 10) }}</td>
                <td class="col-1 text-center text-muted small clickable-cell"
                    :class="{ 'sources-conflict': hasSourceConflict(match.calMatchId) }"
                    :title="hasSourceConflict(match.calMatchId) ? 'A source here overlaps another match\'s channel or encoder stream elsewhere' : ''"
                    @click="toggleSourcesExpand(match.calMatchId)">
                  {{ configuredCount(match.calMatchId) }}/4
                  <span v-if="matchHasNameConflict(match.calMatchId)" class="text-warning" :title="'Channel names: ' + matchNameConflictReason(match.calMatchId)">&#9888;</span>
                  <span class="chevron">{{ isSourcesExpanded(match.calMatchId) ? '▾' : '▸' }}</span>
                </td>
                <td class="col-1 centered">
                  <router-link :to="editLink(match)" class="procricket-btn--reverse btn-xs">Edit</router-link>
                </td>
              </tr>
              <tr>
                <td :colspan="13" class="expand-cell">
                  <transition name="expand">
                    <div v-show="isSourcesExpanded(match.calMatchId)" class="expand-wrapper p-1 cal-sources-panel">
                      <div v-if="matchHasNameConflict(match.calMatchId)" class="alert alert-warning py-1 px-2 small mb-2">
                        &#9888; Channel names don't form one stream: {{ matchNameConflictReason(match.calMatchId) }}.
                        Every channel is offered in the dropdowns until they match.
                      </div>
                      <CalChannelSlotEditor
                          v-for="slot in visibleSlotsFor(match)" :key="slot.key"
                          :source-index="slot.index"
                          :channel="slot.channel"
                          :default-slot="templateSlots[slot.index]"
                          :match-start-time="match.startTime"
                          :match-end-time="match.endTime"
                          :channel-options="channelOptionsForRow(match, slot)"
                          :hardcoded-data="hardcodedData"
                          :channel-conflict="slot.channel && slot.channel.calChannelId ? channelConflicts[slot.channel.calChannelId] : null"
                          :known-channels="calChannelsList"
                          :known-matches="calMatchesList"
                          :saving="!!matchSlotSaving[match.calMatchId + '-' + slot.key]"
                          :error-message="matchSlotErrors[match.calMatchId + '-' + slot.key] || ''"
                          :default-lead-minutes="calendarStatic.defaultLeadMinutes"
                          :default-trail-minutes="calendarStatic.defaultTrailMinutes"
                          :language-options="calendarStatic.languages"
                          :locked="isSourceLocked(match, slot)"
                          :read-only="!matchEditable(match)"
                          @save="(payload) => onMatchSlotSave(match.calMatchId, slot.key, payload)"
                          @remove="onSourceRemove(match.calMatchId)"
                          @go-to-conflict="onGoToConflict">
                      </CalChannelSlotEditor>
                      <a v-if="matchEditable(match) && getVisibleSlotCount(match.calMatchId) < 4" role="button" class="text-link-sm"
                         @click="addSourceSlot(match.calMatchId)">
                        + Add source
                      </a>
                    </div>
                  </transition>
                </td>
              </tr>
            </template>
            <tr v-if="!loading && sortData.length === 0">
              <td :colspan="13" class="text-center text-muted">
                {{ matchFilter === 'upcoming' && calMatchesList.length > 0 ? 'No upcoming matches. Switch to All to see past and inactive ones.' : 'No matches found for this calendar.' }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import XaccelService from "@/services/xaccel-service";
import Header from "@/components/Header.vue";
import CalChannelSlotEditor from "@/components/CalChannelSlotEditor.vue";
import { CalChannel, CalLeague } from "@/model/models";
import { truncateText, normalizeList, fromDateTimeLocal, formatLocalDateTime, formatLocalShortRange, localTimezoneLabel, localDateParamFromIso, isMatchUpcoming } from "@/utils/general-functions";

// The local day the conflicting match starts on.
function conflictDayParam(conflictMatch) {
  return localDateParamFromIso(conflictMatch.startTime);
}

export default {
  name: "CalMatches",
  components: {
    Header,
    CalChannelSlotEditor
  },
  data() {
    return {
      tz: localTimezoneLabel(),
      calLeagueId: this.$route.params.calLeagueId,
      helpOpen: false,
      leagueName: this.$route.params.name,
      togglingLeagueActive: false,
      matchFilter: 'upcoming',
      leagueConfig: {
        calLeagueId: this.$route.params.calLeagueId,
        name: this.$route.params.name,
        shortName: this.$route.params.shortName,
        sport: this.$route.params.sport,
        uid: this.$route.params.uid,
        calendarSourceUrl: this.$route.params.calendarSourceUrl,
        lastImport: this.$route.params.lastImport,
        status: this.$route.params.status === 'true' || this.$route.params.status === true,
        active: this.$route.params.active === 'true' || this.$route.params.active === true
      },
      searchText: '',
      calMatchesList: [],
      calChannelsList: [],
      channelConflicts: {},
      channelOptions: [],
      hardcodedData: { encoders: [], origins: [] },
      calendarStatic: {
        languages: [],
        defaultLeadMinutes: 15,
        defaultTrailMinutes: 15,
        maxPadSetupMinutes: 720
      },
      sortColumn: '',
      sortDirection: 'asc',
      loading: false,
      loadError: '',
      editingCalStatusId: null,
      calStatusSavingId: null,
      highlightedMatchId: null,
      expandedSourcesIds: {},
      allSourcesExpanded: false,
      showConflictsOnly: false,
      expandedBeforeConflicts: null,
      defaultSourcesOpen: false,
      addMatchOpen: false,
      addingMatch: false,
      addMatchError: '',
      newMatch: { team1: '', team2: '', venue: '', startLocal: '', endLocal: '', format: undefined, calStatus: 'CONFIRMED' },
      // Client-side only until "Apply to all matches".
      templateSlots: { 1: null, 2: null, 3: null, 4: null },
      templateSaving: { 1: false, 2: false, 3: false, 4: false },
      templateErrors: { 1: '', 2: '', 3: '', 4: '' },
      applyingToAll: false,
      applyToAllError: '',
      applyResult: null,
      visibleTemplateSlotCount: 1,
      matchSlotSaving: {},
      matchSlotErrors: {},
      visibleSlotCount: {}
    };
  },
  computed: {
    canAddMatch() {
      return !!(this.newMatch.team1.trim() && this.newMatch.team2.trim() && this.newMatch.startLocal);
    },
    headerTitle() {
      const shortName = this.leagueConfig.shortName ? ` (${this.leagueConfig.shortName})` : '';
      return `Matches${this.leagueName ? ` — ${this.leagueName}${shortName}` : ''}`;
    },
    conflictOwnIds() {
      const ids = [];
      for (const m of this.calMatchesList) {
        if (!m.active) continue;
        // The Upcoming view only counts conflicts that can still be fixed; finished matches are in All.
        if (this.matchFilter === 'upcoming' && !this.isUpcomingMatch(m)) continue;
        const channels = this.channelListByMatch[m.calMatchId] || [];
        if (channels.some(c => c.active && this.channelConflicts[c.calChannelId])) ids.push(m.calMatchId);
      }
      return ids;
    },
    conflictMatchCount() {
      return this.conflictOwnIds.length;
    },
    conflictVisibleIds() {
      const loadedActive = new Set(this.calMatchesList
          .filter(m => m.active && (this.matchFilter !== 'upcoming' || this.isUpcomingMatch(m)))
          .map(m => m.calMatchId));
      const ids = new Set(this.conflictOwnIds);
      for (const id of this.conflictOwnIds) {
        for (const c of (this.channelListByMatch[id] || [])) {
          if (!c.active) continue;
          const conflict = this.channelConflicts[c.calChannelId];
          if (!conflict) continue;
          for (const cm of (conflict.conflictingMatches || [])) {
            if (loadedActive.has(cm.calMatchId)) ids.add(cm.calMatchId);
          }
        }
      }
      return ids;
    },
    // Apply to all only touches active matches that have not started yet; the backend enforces the same rule.
    upcomingActiveMatchCount() {
      return this.calMatchesList.filter(match => match.active && isMatchUpcoming(match.startTime)).length;
    },
    filteredCalMatchesList() {
      const search = this.searchText.toLowerCase().trim();
      let filteredList = this.calMatchesList.filter((match) => {
        if (this.showConflictsOnly && !this.conflictVisibleIds.has(match.calMatchId)) return false;
        if (this.matchFilter === 'upcoming' && !this.isUpcomingMatch(match)) return false;
        return (
            (match.calMatchId !== undefined && match.calMatchId !== null && match.calMatchId.toString().includes(search)) ||
            (match.team1 && match.team1.toString().toLowerCase().includes(search)) ||
            (match.team2 && match.team2.toString().toLowerCase().includes(search)) ||
            (match.venue && match.venue.toString().toLowerCase().includes(search)) ||
            (match.format && match.format.toString().toLowerCase().includes(search)) ||
            (match.calStatus && match.calStatus.toString().toLowerCase().includes(search))
        );
      });

      if (this.sortColumn) {
        const direction = this.sortDirection === 'asc' ? 1 : -1;
        filteredList = filteredList.sort((a, b) => {
          const valueA = a[this.sortColumn];
          const valueB = b[this.sortColumn];

          if (valueA < valueB) return -1 * direction;
          if (valueA > valueB) return 1 * direction;
          return 0;
        });
      }
      return filteredList;
    },
    sortData() {
      return this.filteredCalMatchesList;
    },
    channelListByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!map[ch.calMatchId]) map[ch.calMatchId] = [];
        map[ch.calMatchId].push(ch);
      }
      return map;
    },
    slotFamilies() {
      const families = {};
      for (const c of this.sportChannelOptions) {
        const parsed = this.parseSlotChannelName(c.channelName);
        if (!parsed) continue;
        if (!families[parsed.number]) families[parsed.number] = {};
        families[parsed.number][parsed.letter || 'base'] = c;
      }
      return families;
    },
    // Channel dropdowns only offer channels tagged with this calendar's sport (e.g. "cricket"). Both sides
    // are lowercased; the list is application-agnostic. No sport on the calendar, or no channel tagged
    // with it, falls back to every channel so the dropdown never dead-ends.
    sportChannelOptions() {
      const sport = String(this.leagueConfig.sport || '').toLowerCase().trim();
      const tagged = sport
          ? this.channelOptions.filter(c => String(c.tags || '').toLowerCase().includes(sport))
          : this.channelOptions;
      const pool = tagged.length ? tagged : this.channelOptions;
      // Channels come from every application, so the same name can repeat - offer each name once.
      const byName = new Map();
      for (const c of pool) if (!byName.has(c.channelName)) byName.set(c.channelName, c);
      return [...byName.values()].sort((x, y) =>
          String(x.channelName).localeCompare(String(y.channelName), undefined, { numeric: true }));
    },
    hasAnyTemplateSlot() {
      return [1, 2, 3, 4].some(n => this.isSlotConfigured(this.templateSlots[n]));
    },
    templateSlotNumber() {
      for (const n of [1, 2, 3, 4]) {
        const parsed = this.parseSlotChannelName(this.templateSlots[n]?.channelName);
        if (parsed) return parsed.number;
      }
      return null;
    },
  },
  created() {
    this.doLoadAll();
    this.sortColumn = 'startTime';
  },
  methods: {
    truncateText,
    calStatusOptionsFor(match) {
      const list = this.calendarStatic.calStatuses || [];
      return list.includes(match.calStatus) ? list : [match.calStatus, ...list];
    },
    startCalStatusEdit(match) {
      this.editingCalStatusId = match.calMatchId;
      this.$nextTick(() => {
        const el = this.$refs['calStatusSelect' + match.calMatchId];
        const select = Array.isArray(el) ? el[0] : el;
        if (select) { select.focus(); if (select.showPicker) { try { select.showPicker(); } catch (e) { /* needs a user gesture; focus is enough */ } } }
      });
    },
    // The update endpoint takes the whole match, so the current values are sent back unchanged.
    saveCalStatus(match, calStatus) {
      this.editingCalStatusId = null;
      if (calStatus === match.calStatus) return;
      this.loadError = '';
      this.calStatusSavingId = match.calMatchId;
      AdminService.updateCalMatch({
        calMatchId: match.calMatchId,
        team1: match.team1,
        team2: match.team2,
        startTime: match.startTime,
        endTime: match.endTime || null,
        venue: match.venue || null,
        format: match.format || null,
        calStatus
      }).then(() => {
        match.calStatus = calStatus;
      }).catch((error) => {
        this.loadError = this.extractErrorMessage(error);
      }).finally(() => {
        this.calStatusSavingId = null;
      });
    },
    toggleAddMatch() {
      this.addMatchOpen = !this.addMatchOpen;
      this.addMatchError = '';
    },
    addMatch() {
      if (this.addingMatch || !this.canAddMatch) return;
      const startTime = fromDateTimeLocal(this.newMatch.startLocal);
      const endTime = this.newMatch.endLocal ? fromDateTimeLocal(this.newMatch.endLocal) : undefined;
      if (endTime && new Date(endTime) <= new Date(startTime)) {
        this.addMatchError = 'End time must be after the start time.';
        return;
      }
      this.addingMatch = true;
      this.addMatchError = '';
      AdminService.createCalMatch({
        calLeagueId: Number(this.calLeagueId),
        team1: this.newMatch.team1.trim(),
        team2: this.newMatch.team2.trim(),
        venue: this.newMatch.venue.trim() || undefined,
        startTime,
        endTime,
        format: this.newMatch.format,
        calStatus: this.newMatch.calStatus
      }).then(() => {
        return AdminService.getCalMatchesList(Number(this.calLeagueId)).then((matches) => {
          this.calMatchesList = matches;
        });
      }).then(() => {
        this.newMatch = { ...this.newMatch, team1: '', team2: '', venue: '', startLocal: '', endLocal: '' };
      }).catch((error) => {
        this.addMatchError = this.extractErrorMessage(error);
      }).finally(() => {
        this.addingMatch = false;
      });
    },
    doLoadAll() {
      if (!this.calLeagueId) return;
      this.loading = true;
      this.loadError = '';
      this.loadCalendarStaticData();

      const calLeagueId = Number(this.calLeagueId);
      Promise.all([
        AdminService.getCalMatchesList(calLeagueId),
        AdminService.getCalChannelsList(calLeagueId),
        AdminService.getChannelsList(undefined, true),
        XaccelService.getHardcodedData(),
        AdminService.getChannelConflicts(calLeagueId)
      ]).then(([matches, channels, channelOptions, hardcodedData, channelConflicts]) => {
        this.calMatchesList = matches;
        this.calChannelsList = channels;
        this.channelOptions = channelOptions || [];
        // encoders/origins come back as a comma-separated string, not an array.
        this.hardcodedData = {
          encoders: normalizeList((hardcodedData || {}).encoders),
          origins: normalizeList((hardcodedData || {}).origins)
        };
        const conflictMap = {};
        (channelConflicts || []).forEach((c) => { conflictMap[c.calChannelId] = c; });
        this.channelConflicts = conflictMap;
        this.applyOpenMatchQuery();
      }).catch((error) => {
        console.error("Error loading matches/sources:", error);
        this.loadError = this.extractErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    loadCalendarStaticData() {
      AdminService.getCalendarStaticData().then((data) => {
        this.calendarStatic = data;
      }).catch((error) => {
        console.error("Error loading calendar static data:", error);
      });
    },
    parseSlotChannelName(channelName) {
      const m = /^stream_(\d{1,2})([b-d])?$/i.exec(channelName || '');
      if (!m) return null;
      return { number: Number(m[1]), letter: m[2] ? m[2].toLowerCase() : undefined };
    },
    matchSlotNumber(calMatchId) {
      // Active channels decide the match's stream number; inactive ones only as a fallback.
      const channels = (this.channelListByMatch[calMatchId] || []).slice().sort((a, b) => Number(!!b.active) - Number(!!a.active));
      for (const c of channels) {
        const parsed = this.parseSlotChannelName(c.channelName);
        if (parsed) return parsed.number;
      }
      return null;
    },
    baseChannelOptions() {
      return Object.keys(this.slotFamilies)
          .map(Number)
          .sort((x, y) => x - y)
          .filter(n => this.slotFamilies[n].base)
          .map(n => this.slotFamilies[n].base);
    },
    // A set of (active) channel names is "in conflict" when it can't be one stream family: unrecognised names,
    // different stream numbers (stream_1 + stream_3b), or the same slot twice (stream_1 + stream_1).
    // While a match is in conflict its dropdowns offer every channel tagged
    // with the sport so it can be fixed; once fixed, the normal family flow below applies again.
    namesConflictReason(names) {
      const numbers = new Set();
      const slots = new Set();
      for (const name of names) {
        const parsed = this.parseSlotChannelName(name);
        if (!parsed) return `"${name}" is not a stream_N channel`;
        const slotKey = parsed.number + (parsed.letter || '');
        if (slots.has(slotKey)) return `${name} is used more than once`;
        slots.add(slotKey);
        numbers.add(parsed.number);
      }
      return numbers.size > 1 ? `mixed stream numbers (${[...numbers].sort((x, y) => x - y).map(n => 'stream_' + n).join(', ')})` : '';
    },
    namesInConflict(names) {
      return this.namesConflictReason(names) !== '';
    },
    matchNameConflictReason(calMatchId) {
      // Deactivated channels are ignored - deactivating the outliers clears the warning.
      return this.namesConflictReason((this.channelListByMatch[calMatchId] || []).filter(c => c.active).map(c => c.channelName));
    },
    matchHasNameConflict(calMatchId) {
      return this.matchNameConflictReason(calMatchId) !== '';
    },
    templateHasNameConflict() {
      return this.namesInConflict([1, 2, 3, 4].map(i => this.templateSlots[i]).filter(c => c && c.active && c.channelName).map(c => c.channelName));
    },
    // Source 1 offers base channels (stream_1, stream_2...); later sources only the next unused letter
    // of the match's family (stream_1b, then stream_1c). Everything is limited to the calendar's sport.
    channelOptionsForRow(match, slot) {
      const full = this.withSavedChannel(this.sportChannelOptions, slot.channel && slot.channel.channelName);
      if (this.matchHasNameConflict(match.calMatchId)) return full;
      if (slot.channel) {
        const parsed = this.parseSlotChannelName(slot.channel.channelName);
        if (!parsed) return full;
        if (parsed.letter) {
          const family = this.slotFamilies[parsed.number];
          return family && family[parsed.letter] ? [family[parsed.letter]] : full;
        }
        return this.baseChannelOptions();
      }
      const establishedNumber = this.matchSlotNumber(match.calMatchId);
      if (establishedNumber === null) {
        return this.baseChannelOptions();
      }
      const family = this.slotFamilies[establishedNumber];
      if (!family) return full;
      const usedLetters = new Set(
          (this.channelListByMatch[match.calMatchId] || [])
              .map(c => this.parseSlotChannelName(c.channelName))
              .filter(Boolean)
              .map(p => p.letter || 'base')
      );
      const nextLetter = ['base', 'b', 'c', 'd'].find(l => !usedLetters.has(l) && family[l]);
      return nextLetter ? [family[nextLetter]] : [];
    },
    templateChannelOptionsForRow(n) {
      const slot = this.templateSlots[n];
      const full = this.withSavedChannel(this.sportChannelOptions, slot && slot.channelName);
      if (this.templateHasNameConflict()) return full;
      if (slot) {
        const parsed = this.parseSlotChannelName(slot.channelName);
        if (!parsed) return full;
        if (parsed.letter) {
          const family = this.slotFamilies[parsed.number];
          return family && family[parsed.letter] ? [family[parsed.letter]] : full;
        }
        return this.baseChannelOptions();
      }
      const establishedNumber = this.templateSlotNumber;
      if (establishedNumber === null) {
        return this.baseChannelOptions();
      }
      const family = this.slotFamilies[establishedNumber];
      if (!family) return full;
      const usedLetters = new Set(
          [1, 2, 3, 4]
              .map(i => this.templateSlots[i])
              .filter(c => c && c.active)
              .map(c => this.parseSlotChannelName(c.channelName))
              .filter(Boolean)
              .map(p => p.letter || 'base')
      );
      const templateNextLetter = ['base', 'b', 'c', 'd'].find(l => !usedLetters.has(l) && family[l]);
      return templateNextLetter ? [family[templateNextLetter]] : [];
    },
    withSavedChannel(options, channelName) {
      if (channelName && !options.some(c => c.channelName === channelName)) {
        const saved = this.channelOptions.find(c => c.channelName === channelName);
        if (saved) return [...options, saved];
      }
      return options;
    },
    isSlotConfigured(slot) {
      // The channel is optional: a default source left on Automatic has none.
      return !!(slot && slot.origin && slot.encoder && slot.encStreamId);
    },
    configuredCount(calMatchId) {
      const channels = this.channelListByMatch[calMatchId] || [];
      return channels.filter(c => c.active && this.isSlotConfigured(c)).length;
    },
    hasSourceConflict(calMatchId) {
      const channels = this.channelListByMatch[calMatchId] || [];
      return channels.some(c => !!this.channelConflicts[c.calChannelId]);
    },
    onGoToConflict(conflictMatch) {
      const targetId = conflictMatch.calMatchId;
      if (this.calMatchesList.some(m => m.calMatchId === targetId)) {
        this.focusMatch(targetId);
        return;
      }
      const query = { openMatch: String(targetId) };
      const date = conflictDayParam(conflictMatch);
      if (date) query.date = date;
      this.resolveLeagueName(conflictMatch.calLeagueId).then((leagueName) => {
        const where = leagueName ? `the "${leagueName}" calendar` : 'another league';
        const confirmed = window.confirm(
            `This conflict is with a match in ${where}, so it can't be shown here. Open it in Daily to view and fix it?`
        );
        if (confirmed) {
          this.$router.push({ name: 'Daily', query });
        }
      });
    },
    resolveLeagueName(calLeagueId) {
      if (this._leagueNameCache) {
        return Promise.resolve(this._leagueNameCache[calLeagueId] || null);
      }
      return AdminService.getCalLeaguesList(true).then((leagues) => {
        this._leagueNameCache = {};
        (leagues || []).forEach((lg) => { this._leagueNameCache[lg.calLeagueId] = lg.name; });
        return this._leagueNameCache[calLeagueId] || null;
      }).catch(() => null);
    },
    focusMatch(calMatchId) {
      // A past or inactive match is hidden by the default Upcoming view - show everything so the jump lands.
      const target = this.calMatchesList.find(x => x.calMatchId === calMatchId);
      if (target && !this.isUpcomingMatch(target)) this.matchFilter = 'all';
      this.expandedSourcesIds = { ...this.expandedSourcesIds, [calMatchId]: true };
      this.highlightedMatchId = calMatchId;
      clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        if (this.highlightedMatchId === calMatchId) this.highlightedMatchId = null;
      }, 4000);
      this.$nextTick(() => {
        const el = document.getElementById('match-row-' + calMatchId);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    applyOpenMatchQuery() {
      const raw = this.$route.query.openMatch;
      if (!raw) return;
      const targetId = Number(raw);
      if (this.calMatchesList.some(m => m.calMatchId === targetId)) {
        this.focusMatch(targetId);
      }
    },
    visibleMatchChannelRows(calMatchId) {
      const letterRank = { base: 0, b: 1, c: 2, d: 3 };
      return (this.channelListByMatch[calMatchId] || [])
          .slice()
          .sort((a, b) => {
            const pa = this.parseSlotChannelName(a.channelName);
            const pb = this.parseSlotChannelName(b.channelName);
            const ra = pa ? (letterRank[pa.letter || 'base'] ?? 4) : 4;
            const rb = pb ? (letterRank[pb.letter || 'base'] ?? 4) : 4;
            if (ra !== rb) return ra - rb;
            return (a.calChannelId || 0) - (b.calChannelId || 0);
          });
    },
    getVisibleSlotCount(calMatchId) {
      if (this.visibleSlotCount[calMatchId] !== undefined) {
        return this.visibleSlotCount[calMatchId];
      }
      return Math.max(this.visibleMatchChannelRows(calMatchId).length, 1);
    },
    visibleSlotsFor(match) {
      const channels = this.visibleMatchChannelRows(match.calMatchId);
      const visibleCount = this.getVisibleSlotCount(match.calMatchId);
      const slots = channels.map((ch, i) => ({ key: 'ch-' + ch.calChannelId, index: i + 1, channel: ch }));
      for (let i = slots.length; i < visibleCount; i++) {
        slots.push({ key: 'new-' + i, index: i + 1, channel: null });
      }
      return slots;
    },
    addSourceSlot(calMatchId) {
      const current = this.getVisibleSlotCount(calMatchId);
      if (current < 4) {
        this.visibleSlotCount = { ...this.visibleSlotCount, [calMatchId]: current + 1 };
      }
    },
    addTemplateSlot() {
      if (this.visibleTemplateSlotCount < 4) {
        this.visibleTemplateSlotCount += 1;
      }
    },
    isUpcomingMatch(match) {
      return !!match.active && !this.matchFinished(match);
    },
    matchFinished(match) {
      const end = new Date(match.endTime || match.startTime).getTime();
      return !isNaN(end) && end <= Date.now();
    },
    // Sources and events stay editable until the match has finished.
    matchEditable(match) {
      return !this.matchFinished(match);
    },
    isSourceLocked(match, slot) {
      if (slot.index <= 1) return false;
      const prior = this.visibleSlotsFor(match).find(s => s.index === slot.index - 1);
      return !this.isSlotConfigured(prior && prior.channel);
    },
    isTemplateSourceLocked(n) {
      if (n <= 1) return false;
      return !this.isSlotConfigured(this.templateSlots[n - 1]);
    },
    toggleSourcesExpand(calMatchId) {
      this.expandedSourcesIds = { ...this.expandedSourcesIds, [calMatchId]: !this.expandedSourcesIds[calMatchId] };
    },
    isSourcesExpanded(calMatchId) {
      return !!this.expandedSourcesIds[calMatchId];
    },
    toggleShowConflicts() {
      if (!this.showConflictsOnly && this.conflictMatchCount === 0) return;
      this.showConflictsOnly = !this.showConflictsOnly;
      if (this.showConflictsOnly) {
        this.expandedBeforeConflicts = { ...this.expandedSourcesIds };
        const next = { ...this.expandedSourcesIds };
        this.conflictVisibleIds.forEach((id) => { next[id] = true; });
        this.expandedSourcesIds = next;
      } else if (this.expandedBeforeConflicts) {
        this.expandedSourcesIds = this.expandedBeforeConflicts;
        this.expandedBeforeConflicts = null;
      }
    },
    toggleAllSourcesExpand() {
      this.allSourcesExpanded = !this.allSourcesExpanded;
      if (this.allSourcesExpanded) {
        const next = {};
        for (const m of this.calMatchesList) next[m.calMatchId] = true;
        this.expandedSourcesIds = next;
      } else {
        this.expandedSourcesIds = {};
      }
    },
    upsertChannel(calMatchId, payload) {
      const existing = payload.calChannelId
          ? this.calChannelsList.find(c => c.calChannelId === payload.calChannelId)
          : this.calChannelsList.find(c => c.calMatchId === calMatchId && c.channelName === payload.channelName);

      // The backend computes the window from the match: lead before its start, trail after its end (start when none).
      const leadMinutes = payload.leadMinutes !== undefined ? Math.abs(payload.leadMinutes || 0) : (existing ? existing.leadMinutes : undefined);
      const trailMinutes = payload.trailMinutes !== undefined ? Math.abs(payload.trailMinutes || 0) : (existing ? existing.trailMinutes : undefined);
      const calChannel = new CalChannel(
          existing ? existing.calChannelId : undefined,
          calMatchId,
          payload.encoder,
          payload.encStreamId,
          payload.encStreamName,
          payload.channelName,
          payload.origin,
          payload.orderId !== undefined ? payload.orderId : (existing ? existing.orderId : undefined),
          payload.language,
          leadMinutes,
          trailMinutes,
          existing ? existing.status : false,
          payload.active
      );
      return AdminService.upsertCalChannel(calChannel).then(() => calChannel);
    },
    // Conflicts are computed BE-side, so they're refetched together with the channels.
    refetchChannels() {
      const channelsFetch = AdminService.getCalChannelsList(Number(this.calLeagueId));
      const conflictsFetch = AdminService.getChannelConflicts(Number(this.calLeagueId));
      return Promise.all([channelsFetch, conflictsFetch]).then(([channels, channelConflicts]) => {
        this.calChannelsList = channels;
        const conflictMap = {};
        (channelConflicts || []).forEach((c) => { conflictMap[c.calChannelId] = c; });
        this.channelConflicts = conflictMap;
      });
    },
    saveSlot(calMatchId, payload) {
      return this.upsertChannel(calMatchId, payload).then((calChannel) => {
        return this.refetchChannels().then(() => calChannel);
      });
    },
    // Source 1 moved to a new number: siblings move to the same letter in the new family, or get deactivated.
    cascadeSlotFamilyChange(calMatchId, newNumber, savedCalChannelId) {
      const family = this.slotFamilies[newNumber];
      const siblings = this.visibleMatchChannelRows(calMatchId)
          .filter(c => c.calChannelId !== savedCalChannelId);
      const tasks = siblings.map((c) => {
        const parsed = this.parseSlotChannelName(c.channelName);
        const letter = parsed ? (parsed.letter || 'base') : null;
        const target = letter && letter !== 'base' && family ? family[letter] : null;
        return this.upsertChannel(calMatchId, {
          calChannelId: c.calChannelId,
          channelName: target ? target.channelName : c.channelName,
          origin: c.origin,
          encoder: c.encoder,
          encStreamId: c.encStreamId,
          encStreamName: c.encStreamName,
          language: c.language,
          leadMinutes: c.leadMinutes,
          trailMinutes: c.trailMinutes,
          active: target ? c.active : false
        });
      });
      return tasks.length ? Promise.allSettled(tasks) : Promise.resolve();
    },
    onMatchSlotSave(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      const parsed = this.parseSlotChannelName(payload.channelName);
      const isFamilyNumberChange = parsed && !parsed.letter;
      const savedChannel = payload.calChannelId
          ? this.calChannelsList.find(c => c.calChannelId === payload.calChannelId) : null;
      const savedParsed = savedChannel ? this.parseSlotChannelName(savedChannel.channelName) : null;
      const previousNumber = isFamilyNumberChange
          ? (savedParsed ? savedParsed.number : this.matchSlotNumber(calMatchId)) : null;
      this.matchSlotSaving = { ...this.matchSlotSaving, [key]: true };
      this.matchSlotErrors = { ...this.matchSlotErrors, [key]: '' };
      this.saveSlot(calMatchId, payload).then((calChannel) => {
        if (isFamilyNumberChange && previousNumber !== null && previousNumber !== parsed.number) {
          return this.cascadeSlotFamilyChange(calMatchId, parsed.number, calChannel.calChannelId)
              .then(() => this.refetchChannels());
        }
      }).catch((error) => {
        this.matchSlotErrors = { ...this.matchSlotErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchSlotSaving = { ...this.matchSlotSaving, [key]: false };
      });
    },
    onSourceRemove(calMatchId) {
      const previousCount = this.getVisibleSlotCount(calMatchId);
      this.visibleSlotCount = { ...this.visibleSlotCount, [calMatchId]: Math.max(previousCount - 1, 0) };
    },
    formatShortRange(match) {
      return formatLocalShortRange(match.startTime, match.endTime);
    },
    onTemplateSlotSave(payload) {
      const parsed = this.parseSlotChannelName(payload.channelName);
      const isFamilyNumberChange = parsed && !parsed.letter;
      const previousNumber = isFamilyNumberChange ? this.templateSlotNumber : null;
      const nextSlots = { ...this.templateSlots, [payload.sourceIndex]: payload };
      if (isFamilyNumberChange && previousNumber !== null && previousNumber !== parsed.number) {
        const family = this.slotFamilies[parsed.number];
        for (const n of [2, 3, 4]) {
          const sibling = nextSlots[n];
          if (!sibling) continue;
          const siblingParsed = this.parseSlotChannelName(sibling.channelName);
          const letter = siblingParsed ? (siblingParsed.letter || 'base') : null;
          const target = letter && letter !== 'base' && family ? family[letter] : null;
          nextSlots[n] = target ? { ...sibling, channelName: target.channelName } : null;
        }
      }
      this.templateSlots = nextSlots;
      this.templateSaving = { ...this.templateSaving, [payload.sourceIndex]: true };
      this.$nextTick(() => {
        this.templateSaving = { ...this.templateSaving, [payload.sourceIndex]: false };
      });
    },
    applyTemplateToAll() {
      if (this.applyingToAll) return;
      const configuredSlots = [1, 2, 3, 4].map(n => this.templateSlots[n]).filter(s => this.isSlotConfigured(s));
      if (!configuredSlots.length || this.upcomingActiveMatchCount === 0) return;

      const confirmed = window.confirm(
          `Apply ${configuredSlots.length} default source${configuredSlots.length === 1 ? '' : 's'} to the ${this.upcomingActiveMatchCount} upcoming match${this.upcomingActiveMatchCount === 1 ? '' : 'es'} of this calendar? ` +
          `Matches that have already started are not changed. ` +
          `Matches that overlap each other, or clash with sources that already exist, are left unchanged and listed below.`
      );
      if (!confirmed) return;

      this.applyingToAll = true;
      this.applyToAllError = '';
      this.applyResult = null;

      const sources = configuredSlots.map(slot => ({
        channelName: slot.channelName,
        origin: slot.origin,
        encoder: slot.encoder,
        encStreamId: slot.encStreamId,
        encStreamName: slot.encStreamName,
        language: slot.language,
        leadMinutes: Math.abs(slot.leadMinutes || 0),
        trailMinutes: Math.abs(slot.trailMinutes || 0),
        active: slot.active !== false
      }));

      AdminService.applyDefaultSources(Number(this.calLeagueId), sources).then((result) => {
        this.applyResult = result;
        return this.refetchChannels();
      }).catch((error) => {
        this.applyToAllError = this.extractErrorMessage(error);
      }).finally(() => {
        this.applyingToAll = false;
      });
    },
    notSetReasonLabel(reason) {
      if (reason === 'OVERLAPS_LEAGUE_MATCH') return 'Encoder stream shared with another match of this calendar';
      if (reason === 'NO_FREE_CHANNEL') return 'No free channel';
      if (reason === 'ON_AIR') return 'On air - its sources cannot be changed';
      return 'Encoder stream already in use';
    },
    dayLabel(match) {
      if (!match.parentUid) return '—';
      const siblings = this.calMatchesList
          .filter(m => m.parentUid === match.parentUid && m.calLeagueId === match.calLeagueId)
          .sort((a, b) => (a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0));
      if (siblings.length < 2) return '—';
      const idx = siblings.findIndex(m => m.calMatchId === match.calMatchId);
      return `Day ${idx + 1} of ${siblings.length}`;
    },
    formatDateTime(iso) {
      return formatLocalDateTime(iso);
    },
    editLink(match) {
      return { name: 'CalMatchEdit', params: { calMatchId: match.calMatchId } };
    },
    toggleLeagueActive() {
      if (this.togglingLeagueActive) return;
      const activating = !this.leagueConfig.active;
      const message = activating
          ? `Reactivate "${this.leagueConfig.name}"? It will show in the Active calendars list again.`
          : `Deactivate "${this.leagueConfig.name}"? It will be hidden from the Active calendars list. Its matches and sources are not changed.`;
      if (!window.confirm(message)) return;

      const c = this.leagueConfig;
      const league = new CalLeague(Number(c.calLeagueId), c.name, c.shortName, c.sport, c.uid,
          c.calendarSourceUrl, c.lastImport, c.status, activating);
      this.togglingLeagueActive = true;
      AdminService.updateCalLeague(league).then(() => {
        this.leagueConfig.active = activating;
      }).catch((error) => {
        window.alert(this.extractErrorMessage(error));
      }).finally(() => {
        this.togglingLeagueActive = false;
      });
    },
    backToCalendars() {
      this.$router.push({ name: 'CalLeagues' });
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  },
};
</script>
<style src="../../assets/style.css">
</style>
